// SkuCountersCard — per-bucket 連番 counter management.
// Extracted from SkuMasterSettings.js in iter_145.
// iter_148 · Visual polish: card grid, sortable by name / next_serial,
// filter chips (アクティブ = next_serial>0, まだ発番なし = ==0), color hue
// signals how "hot" a bucket is, inline reset with clear warning tint.
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ArrowsClockwise, Warning, MagnifyingGlass, Database, ArrowSquareOut } from '@phosphor-icons/react';
import { formatShort } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SkuCountersCard({ onDrillDown }) {
  const [counters, setCounters] = useState([]);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');     // 'all' | 'active' | 'empty'
  const [sort, setSort] = useState('recent');      // 'recent' | 'bucket' | 'serial'
  const [busy, setBusy] = useState(null);
  const [resetVal, setResetVal] = useState({});

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/sku/counters`, { withCredentials: true });
    setCounters(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let out = s ? counters.filter((c) => c.id.toLowerCase().includes(s)) : [...counters];
    if (filter === 'active') out = out.filter((c) => c.next_serial > 0);
    else if (filter === 'empty') out = out.filter((c) => !c.next_serial);
    out.sort((a, b) => {
      if (sort === 'bucket') return a.id.localeCompare(b.id);
      if (sort === 'serial') return (b.next_serial || 0) - (a.next_serial || 0);
      // 'recent'
      const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return tb - ta;
    });
    return out;
  }, [counters, q, filter, sort]);

  const active = counters.filter((c) => c.next_serial > 0).length;
  const empty = counters.length - active;

  const doReset = async (bucket) => {
    const n = parseInt(resetVal[bucket] ?? 0);
    if (isNaN(n) || n < 0) { toast.error('0 以上の整数を入力してください'); return; }
    if (!window.confirm(`カウンター [${bucket}] を ${n} にリセットしますか？\n次回発番はこの値からとなります。`)) return;
    setBusy(bucket);
    try {
      await axios.post(`${API}/sku/counters/reset`, { bucket, next_serial: n }, { withCredentials: true });
      toast.success('リセットしました');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'リセット失敗');
    } finally {
      setBusy(null);
    }
  };

  // Color-code cards by how "hot" the counter is.
  const heatTone = (n) => {
    if (!n) return { chip: 'bg-zinc-100 text-zinc-500', border: 'border-zinc-200', num: 'text-zinc-400' };
    if (n >= 1000) return { chip: 'bg-rose-100 text-rose-700', border: 'border-rose-200', num: 'text-rose-700' };
    if (n >= 100) return { chip: 'bg-amber-100 text-amber-700', border: 'border-amber-200', num: 'text-amber-700' };
    return { chip: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-200', num: 'text-emerald-700' };
  };

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-zinc-700 flex items-center gap-1.5">
            <Database size={16} weight="duotone" className="text-amber-600" />
            カウンター管理 <Badge variant="secondary" className="ml-1 bg-amber-100 text-amber-800 border-amber-200">{filtered.length}/{counters.length}</Badge>
          </h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">バケット別の次回連番。リセットすると次の発番はここで設定した値になります。</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex gap-1 rounded-lg bg-zinc-100 p-1">
            <button onClick={() => setSort('recent')}  className={`px-2 h-7 rounded text-[11px] font-semibold ${sort === 'recent' ? 'bg-white shadow-sm text-amber-700' : 'text-zinc-500 hover:text-zinc-700'}`} data-testid="sku-counter-sort-recent">最近</button>
            <button onClick={() => setSort('serial')}  className={`px-2 h-7 rounded text-[11px] font-semibold ${sort === 'serial' ? 'bg-white shadow-sm text-amber-700' : 'text-zinc-500 hover:text-zinc-700'}`} data-testid="sku-counter-sort-serial">連番大</button>
            <button onClick={() => setSort('bucket')}  className={`px-2 h-7 rounded text-[11px] font-semibold ${sort === 'bucket' ? 'bg-white shadow-sm text-amber-700' : 'text-zinc-500 hover:text-zinc-700'}`} data-testid="sku-counter-sort-bucket">名前</button>
          </div>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="バケット検索…" className="pl-7 h-9 w-56" data-testid="sku-counter-search" />
          </div>
        </div>
      </div>

      {/* Filter chips */}
      {counters.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="sku-counter-filter-chips">
          {[
            { key: 'all',    label: 'すべて',       count: counters.length },
            { key: 'active', label: 'アクティブ',   count: active },
            { key: 'empty',  label: 'まだ発番なし', count: empty },
          ].map((chip) => (
            <button
              key={chip.key}
              onClick={() => setFilter(chip.key)}
              data-testid={`sku-counter-filter-${chip.key}`}
              className={`h-8 px-3 rounded-full border text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                filter === chip.key
                  ? 'bg-amber-600 text-white border-amber-600'
                  : 'bg-white text-zinc-700 border-zinc-300 hover:border-amber-400'
              }`}
            >
              <span>{chip.label}</span>
              <span className={`text-[10px] rounded-full px-1.5 ${filter === chip.key ? 'bg-white/25' : 'bg-zinc-100'}`}>{chip.count}</span>
            </button>
          ))}
        </div>
      )}

      {counters.length === 0 ? (
        <div className="text-center py-10 text-zinc-400 text-sm">
          まだカウンターはありません（発番すると自動作成されます）
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-zinc-400 text-sm">
          {q ? `「${q}」に一致するカウンターはありません` : '該当するカウンターはありません'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[500px] overflow-y-auto pr-1" data-testid="sku-counter-grid">
          {filtered.map((c) => {
            const tone = heatTone(c.next_serial);
            return (
              <div
                key={c.id}
                data-testid={`sku-counter-row-${c.id}`}
                className={`rounded-xl border bg-white p-3 ${tone.border} hover:shadow-sm transition-all`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => onDrillDown?.(c.id.split(':')[0])}
                    className="group/dd flex items-center gap-1 min-w-0 text-left"
                    title={`品番ルール「${c.id.split(':')[0]}」を表示`}
                    data-testid={`sku-counter-drill-${c.id}`}
                  >
                    <span className="font-mono text-xs text-zinc-600 truncate group-hover/dd:text-amber-700 group-hover/dd:underline">{c.id}</span>
                    <ArrowSquareOut size={11} className="shrink-0 text-zinc-300 group-hover/dd:text-amber-600" />
                  </button>
                  <span className={`font-mono font-bold text-lg ${tone.num}`}>{c.next_serial}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-zinc-400">
                    {c.updated_at ? formatShort(c.updated_at) : '未更新'}
                  </span>
                  <div className="flex items-center gap-1">
                    <Input value={resetVal[c.id] ?? ''} onChange={(e) => setResetVal({ ...resetVal, [c.id]: e.target.value })}
                           placeholder="0" className="h-7 w-16 text-center font-mono text-xs" data-testid={`sku-counter-reset-input-${c.id}`} />
                    <Button size="sm" variant="outline" onClick={() => doReset(c.id)} disabled={busy === c.id}
                            className="h-7 text-rose-600 border-rose-200 hover:bg-rose-50 text-[11px]"
                            data-testid={`sku-counter-reset-btn-${c.id}`}>
                      {busy === c.id ? <ArrowsClockwise className="animate-spin" size={11} /> : <Warning size={11} />}
                      <span className="ml-0.5">リセット</span>
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
